import React from 'react';
import Icon from '../AppIcon';

const UploadProgressIndicator = ({ 
  progress = 0, 
  stage = 'uploading', 
  fileName = '', 
  estimatedTime = null,
  onCancel = null 
}) => {
  const stages = {
    uploading: { label: 'Uploading image...', icon: 'Upload' },
    processing: { label: 'Processing image...', icon: 'Cpu' },
    analyzing: { label: 'Analyzing bike components...', icon: 'Search' },
    generating: { label: 'Generating recommendations...', icon: 'Zap' },
    complete: { label: 'Analysis complete!', icon: 'CheckCircle' },
    error: { label: 'Upload failed', icon: 'AlertCircle' }
  };

  const currentStage = stages?.[stage] || stages?.uploading;
  const isComplete = stage === 'complete';
  const isError = stage === 'error';

  const formatTime = (seconds) => {
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}m ${remainingSeconds}s`;
  };

  return (
    <div className="w-full max-w-md mx-auto bg-card border border-border rounded-lg p-6 shadow-md animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-3">
          <div className={`p-2 rounded-full ${
            isError ? 'bg-error/10' : 
            isComplete ? 'bg-success/10': 'bg-accent/10'
          }`}>
            <Icon 
              name={currentStage?.icon} 
              size={20} 
              color={
                isError ? 'var(--color-error)' : 
                isComplete ? 'var(--color-success)': 'var(--color-accent)'
              }
            />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-foreground">
              {currentStage?.label}
            </h3>
            {fileName && (
              <p className="text-xs text-muted-foreground truncate max-w-48">
                {fileName}
              </p>
            )}
          </div>
        </div>
        
        {onCancel && !isComplete && !isError && (
          <button
            onClick={onCancel}
            className="p-1 hover:bg-muted rounded-full transition-colors interactive-scale"
            aria-label="Cancel upload"
          >
            <Icon name="X" size={16} color="var(--color-muted-foreground)" />
          </button>
        )}
      </div>
      {/* Progress Bar */}
      {!isError && (
        <div className="mb-4">
          <div className="flex justify-between items-center mb-2">
            <span className="text-xs text-muted-foreground">
              {Math.round(progress)}% complete
            </span>
            {estimatedTime && !isComplete && (
              <span className="text-xs text-muted-foreground">
                ~{formatTime(estimatedTime)} remaining
              </span>
            )}
          </div>
          
          <div className="w-full bg-muted rounded-full h-2 overflow-hidden">
            <div 
              className={`h-full transition-all duration-300 ease-out ${
                isComplete ? 'bg-success' : 'bg-accent'
              }`}
              style={{ width: `${Math.min(progress, 100)}%` }}
            >
              {!isComplete && progress > 0 && progress < 100 && (
                <div className="h-full w-full progress-shimmer"></div>
              )}
            </div>
          </div>
        </div>
      )}
      {/* Stage Indicators */}
      {!isError && (
        <div className="flex justify-between items-center">
          {Object.entries(stages)?.slice(0, -2)?.map(([key, stageInfo], index) => {
            const isCurrentStage = key === stage;
            const isPastStage = ['uploading', 'processing', 'analyzing', 'generating', 'complete']?.indexOf(stage) > 
                               ['uploading', 'processing', 'analyzing', 'generating', 'complete']?.indexOf(key);
            const isActiveStage = isCurrentStage || isPastStage;

            return (
              <div key={key} className="flex flex-col items-center">
                <div className={`w-3 h-3 rounded-full border-2 transition-all duration-200 ${
                  isActiveStage 
                    ? 'bg-accent border-accent' :'bg-background border-muted-foreground/30'
                }`}>
                  {isPastStage && (
                    <div className="w-full h-full flex items-center justify-center">
                      <Icon name="Check" size={8} color="white" />
                    </div>
                  )}
                </div>
                <span className={`text-xs mt-1 transition-colors ${
                  isActiveStage ? 'text-accent' : 'text-muted-foreground'
                }`}>
                  {stageInfo?.label?.split(' ')?.[0]}
                </span>
                {index < 3 && (
                  <div className={`absolute w-8 h-0.5 mt-1.5 ml-6 transition-colors ${
                    isPastStage ? 'bg-accent' : 'bg-muted-foreground/30'
                  }`} />
                )}
              </div>
            );
          })}
        </div>
      )}
      {/* Error State */}
      {isError && (
        <div className="text-center">
          <p className="text-sm text-error mb-3">
            Something went wrong during upload. Please try again.
          </p>
          <div className="flex space-x-2 justify-center">
            <button
              onClick={onCancel}
              className="px-4 py-2 text-sm bg-muted text-muted-foreground rounded-md hover:bg-muted/80 transition-colors interactive-scale"
            >
              Cancel
            </button>
            <button
              onClick={() => window.location?.reload()}
              className="px-4 py-2 text-sm bg-accent text-accent-foreground rounded-md hover:bg-accent/90 transition-colors interactive-scale"
            >
              Try Again
            </button>
          </div>
        </div>
      )}
      {/* Success State */}
      {isComplete && (
        <div className="text-center">
          <p className="text-sm text-success mb-3">
            Your bike analysis is ready! View your personalized upgrade recommendations.
          </p>
          <button
            onClick={() => window.location.href = '/bike-analysis-results'}
            className="px-4 py-2 text-sm bg-success text-success-foreground rounded-md hover:bg-success/90 transition-colors interactive-scale"
          >
            View Results
          </button>
        </div>
      )}
    </div>
  );
};

export default UploadProgressIndicator;